'use client';

/**
 * TCO Workflow Template Creation Page
 *
 * Page for creating new workflow templates for care coordination.
 * Integrates with the full TEO service referral lifecycle including:
 * - Provider matching configuration
 * - SDOH assessment integration
 * - Pricing and billing settings
 * - Communication preferences
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Loader2,
  Bot,
  Zap,
  MessageSquare,
  Users,
  Building2,
  DollarSign,
  MapPin,
  Search,
  Network,
  FileCheck,
  ClipboardList,
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Workflow,
  HeartPulse,
} from 'lucide-react';
import { StepTypePicker } from '@/components/workflows/StepTypePicker';
import { StepConfigurationPanel } from '@/components/workflows/StepConfigurationPanel';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { firstNotifyStepProblem, notifyStepIssues, workflowSaveErrorMessage } from '@/components/workflows/notify/notifySteps';
import { firstMessageStepVariableProblem } from '@/components/workflows/messageStepVariables';
import { WorkflowFlowView } from '@/components/workflows/WorkflowFlowView';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import { apiClient } from '@/services/apiClient';
import { tcoWorkflowsAPI, WorkflowTemplateCreate, TriggerType } from '@/services/api/tcoServiceReferrals';
import { useTabbedChrome } from '@/components/weavein';

// SDOH Domain options
type SDOHDomainCode = 'HOUSING' | 'FOOD_NUTRITION' | 'TRANSPORTATION' | 'ECONOMIC_STABILITY' | 'EDUCATION_EMPLOYMENT' | 'SOCIAL_SUPPORT' | 'HEALTH_BEHAVIORS' | 'SAFETY';

const SDOH_DOMAINS: { value: SDOHDomainCode; label: string }[] = [
  { value: 'HOUSING', label: 'Housing Instability' },
  { value: 'FOOD_NUTRITION', label: 'Food & Nutrition' },
  { value: 'TRANSPORTATION', label: 'Transportation' },
  { value: 'ECONOMIC_STABILITY', label: 'Economic Stability' },
  { value: 'EDUCATION_EMPLOYMENT', label: 'Education & Employment' },
  { value: 'SOCIAL_SUPPORT', label: 'Social Support' },
  { value: 'HEALTH_BEHAVIORS', label: 'Health Behaviors' },
  { value: 'SAFETY', label: 'Safety' },
];

// Trigger type options - must match backend TriggerType enum
const TRIGGER_TYPES: { value: TriggerType; label: string; description: string; icon: React.ReactNode }[] = [
  {
    value: 'clinical_signal_detected',
    label: 'Clinical Signal (Health / EHR)',
    description: 'Health data initiates the loop — an approved AI insight, EHR/claims diagnosis, or observation threshold',
    icon: <HeartPulse className="w-5 h-5" />,
  },
  {
    value: 'sdoh_threshold',
    label: 'SDOH Threshold',
    description: 'Trigger when SDOH screening identifies high-risk domains',
    icon: <Users className="w-5 h-5" />,
  },
  {
    value: 'care_gap_detected',
    label: 'Care Gap Detected',
    description: 'Trigger when a care gap is identified for the member',
    icon: <Zap className="w-5 h-5" />,
  },
  {
    value: 'enrollment_milestone',
    label: 'Enrollment Milestone',
    description: 'Trigger on enrollment milestone events',
    icon: <MessageSquare className="w-5 h-5" />,
  },
  {
    value: 'ai_recommended',
    label: 'AI Recommended',
    description: 'Trigger based on AI-driven recommendations',
    icon: <Bot className="w-5 h-5" />,
  },
  {
    value: 'manual',
    label: 'Manual',
    description: 'Manually triggered by navigator',
    icon: <ClipboardList className="w-5 h-5" />,
  },
];

// Channel options
const CHANNEL_OPTIONS = [
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone Call' },
  { value: 'portal', label: 'Member Portal' },
];

// Prebuilt starter templates — prefill the builder with connected, agent-driven steps.
interface StarterTemplate {
  id: string;
  name: string;
  description: string;
  triggerType: TriggerType;
  steps: Array<{ name: string; step_type: string; config: Record<string, any> }>;
}
const STARTER_TEMPLATES: StarterTemplate[] = [
  {
    id: 'clinical_social',
    name: 'Clinical → Social closed loop',
    description: 'Health insight / EHR diagnosis / observation threshold → clinician gate → social referral(s) → multi-channel outreach → PCP loop-back.',
    triggerType: 'clinical_signal_detected',
    steps: [
      { name: 'Clinical signal detected', step_type: 'clinical_signal', config: { sources: ['approved_insight', 'icd_codes', 'observation_threshold'] } },
      { name: 'Clinician approval', step_type: 'human_approval', config: { role: 'clinician', on_reject: 'end' } },
      { name: 'Create social referral(s)', step_type: 'create_referral', config: { source: 'icd_to_sdoh_mapping', multi: true } },
      { name: 'Member outreach', step_type: 'multi_channel_escalation', config: { mode: 'auto', channels: ['sms', 'voice', 'email'], escalate_after_hours: 24 } },
      { name: 'Notify PCP (loop-back)', step_type: 'notify_pcp', config: { channel: 'portal', include_outcome: true } },
    ],
  },
  {
    id: 'provider_ops',
    name: 'Provider find, review & book',
    description: 'AI finds & ranks providers → pricing negotiation → approval gate → referral → member outreach.',
    triggerType: 'manual',
    steps: [
      { name: 'Find & suggest providers', step_type: 'provider_match', config: { mode: 'auto', max_providers: 5, prefer_network: true } },
      { name: 'Pricing negotiation', step_type: 'pricing_agent', config: { strategy: 'balanced', max_rounds: 5 } },
      { name: 'Approval', step_type: 'human_approval', config: { role: 'supervisor', on_reject: 'end' } },
      { name: 'Create referral', step_type: 'create_referral', config: { source: 'manual', multi: false } },
      { name: 'Member outreach', step_type: 'multi_channel_escalation', config: { mode: 'escalate', channels: ['sms', 'email'] } },
    ],
  },
  {
    id: 'sdoh_outreach',
    name: 'SDOH outreach',
    description: 'SDOH risk threshold → multi-channel member outreach → wait → referral.',
    triggerType: 'sdoh_threshold',
    steps: [
      { name: 'Member outreach', step_type: 'multi_channel_escalation', config: { mode: 'auto', channels: ['sms', 'email'] } },
      { name: 'Wait for response', step_type: 'wait', config: { duration_hours: 48 } },
      { name: 'Create referral', step_type: 'create_referral', config: { source: 'manual', multi: true } },
    ],
  },
];

// Provider matching mode options
const PROVIDER_MATCHING_MODES = [
  { value: 'auto', label: 'Auto-Match', description: 'AI automatically finds and ranks best providers', icon: <Bot className="w-5 h-5" /> },
  { value: 'manual', label: 'Manual Selection', description: 'Navigator manually selects provider', icon: <Search className="w-5 h-5" /> },
  { value: 'network_only', label: 'Network Only', description: 'Match only from contracted network providers', icon: <Network className="w-5 h-5" /> },
  { value: 'cbo_only', label: 'CBO Only', description: 'Match only from community-based organizations', icon: <Building2 className="w-5 h-5" /> },
];

// Urgency level options
const URGENCY_OPTIONS = [
  { value: 'routine', label: 'Routine', description: '14+ days to service' },
  { value: 'urgent', label: 'Urgent', description: '24-72 hours to service' },
  { value: 'emergent', label: 'Emergent', description: 'Same-day service needed' },
];

interface NewWorkflowTemplatePageProps {
  /** Callback when back button is clicked */
  onBack?: () => void;
  /** Callback when template is successfully created */
  onSuccess?: (templateId: number) => void;
}

export default function NewWorkflowTemplatePage({
  onBack,
  onSuccess,
}: NewWorkflowTemplatePageProps = {}) {
  const tabbed = useTabbedChrome();
  const navigate = useNavigate();

  // Use onBack if provided, otherwise fall back to browser navigation
  const handleBack = onBack || (() => navigate(-1));
  const { toast } = useToast();
  const { t } = useAppTranslation();
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  // Basic Information
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [targetEntityType, setTargetEntityType] = useState<'member' | 'contact' | 'both'>('member');

  // Trigger Configuration
  const [triggerType, setTriggerType] = useState<TriggerType>('sdoh_threshold');
  const [autoTriggerEnabled, setAutoTriggerEnabled] = useState(true);
  const [selectedSdohDomains, setSelectedSdohDomains] = useState<SDOHDomainCode[]>([]);
  const [minRiskScore, setMinRiskScore] = useState(70);

  // Outreach Settings
  const [defaultChannel, setDefaultChannel] = useState('sms');
  const [maxOutreachAttempts, setMaxOutreachAttempts] = useState(3);
  const [responseTimeoutHours, setResponseTimeoutHours] = useState(48);
  const [useMlTiming, setUseMlTiming] = useState(false);

  // Referral Settings
  const [autoCreateReferral, setAutoCreateReferral] = useState(true);
  const [referralUrgencyDefault, setReferralUrgencyDefault] = useState('routine');

  // Provider Matching Settings
  const [providerMatchingMode, setProviderMatchingMode] = useState('auto');
  const [maxProvidersToMatch, setMaxProvidersToMatch] = useState(5);
  const [preferNetworkProviders, setPreferNetworkProviders] = useState(true);
  const [cboOnlyForSdoh, setCboOnlyForSdoh] = useState(true);
  const [maxDistanceMiles, setMaxDistanceMiles] = useState(25);
  const [requireAcceptance, setRequireAcceptance] = useState(true);
  const [acceptanceTimeoutHours, setAcceptanceTimeoutHours] = useState(48);

  // Pricing & Billing Settings
  const [enablePricingAgent, setEnablePricingAgent] = useState(true);
  const [pricingStrategy, setPricingStrategy] = useState('balanced');
  const [maxNegotiationRounds, setMaxNegotiationRounds] = useState(5);
  const [autoBilling, setAutoBilling] = useState(true);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState('invoice');

  // Workflow Steps (visual builder)
  interface WorkflowStepDef {
    name: string;
    step_type: string;
    order: number;
    config: Record<string, any>;
    form_template_id?: number | null;
    completion_requires_form?: boolean;
    documentation_required?: boolean;
    document_types?: string[];
    incentive_milestone_code?: string;
    is_critical?: boolean;
  }
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStepDef[]>([]);
  const [editingStepIndex, setEditingStepIndex] = useState<number | null>(null);
  const [addingStep, setAddingStep] = useState(false);
  const [stepsViewMode, setStepsViewMode] = useState<'list' | 'flow'>('list');
  const [newStepType, setNewStepType] = useState('');
  const [newStepName, setNewStepName] = useState('');
  const [newStepConfig, setNewStepConfig] = useState<Record<string, any>>({});

  const addStep = () => {
    if (!newStepType || !newStepName.trim()) return;
    const step: WorkflowStepDef = {
      name: newStepName,
      step_type: newStepType,
      order: workflowSteps.length + 1,
      config: newStepConfig,
      form_template_id: newStepConfig.form_template_id || null,
      completion_requires_form: !!newStepConfig.form_template_id,
    };
    setWorkflowSteps([...workflowSteps, step]);
    setAddingStep(false);
    setNewStepType('');
    setNewStepName('');
    setNewStepConfig({});
  };

  const updateStep = (index: number) => {
    const updated = [...workflowSteps];
    updated[index] = {
      ...updated[index],
      name: newStepName || updated[index].name,
      step_type: newStepType || updated[index].step_type,
      config: newStepConfig,
      form_template_id: newStepConfig.form_template_id || null,
      completion_requires_form: !!newStepConfig.form_template_id,
    };
    setWorkflowSteps(updated);
    setEditingStepIndex(null);
    setNewStepType('');
    setNewStepName('');
    setNewStepConfig({});
  };

  const removeStep = (index: number) => {
    const updated = workflowSteps.filter((_, i) => i !== index);
    updated.forEach((s, i) => (s.order = i + 1));
    setWorkflowSteps(updated);
  };

  const moveStep = (index: number, direction: 'up' | 'down') => {
    const newIdx = direction === 'up' ? index - 1 : index + 1;
    if (newIdx < 0 || newIdx >= workflowSteps.length) return;
    const updated = [...workflowSteps];
    [updated[index], updated[newIdx]] = [updated[newIdx], updated[index]];
    updated.forEach((s, i) => (s.order = i + 1));
    setWorkflowSteps(updated);
  };

  const startEditStep = (index: number) => {
    const step = workflowSteps[index];
    setEditingStepIndex(index);
    setNewStepName(step.name);
    setNewStepType(step.step_type);
    setNewStepConfig(step.config || {});
  };

  // Service Types (for referral creation)
  const [serviceTypes, setServiceTypes] = useState<{ code: string; name: string }[]>([]);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>([]);
  const [loadingServiceTypes, setLoadingServiceTypes] = useState(true);

  // Load service types on mount
  useEffect(() => {
    const loadServiceTypes = async () => {
      try {
        const response = await apiClient.get('/api/service-referrals/taxonomy/types');
        setServiceTypes(response.data || []);
      } catch (err) {
        console.error('Failed to load service types:', err);
      } finally {
        setLoadingServiceTypes(false);
      }
    };
    loadServiceTypes();
  }, []);

  const toggleSdohDomain = (domain: SDOHDomainCode) => {
    setSelectedSdohDomains((prev) =>
      prev.includes(domain) ? prev.filter((d) => d !== domain) : [...prev, domain]
    );
  };

  const toggleServiceType = (code: string) => {
    setSelectedServiceTypes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleSave = async () => {
    console.log('[TCO Template] handleSave called');

    if (!templateName.trim()) {
      console.log('[TCO Template] Validation failed: template name empty');
      toast({
        title: 'Validation Error',
        description: 'Template name is required',
        variant: 'destructive',
      });
      return;
    }

    // TRUS-686: the server refuses a notify step it cannot run (an unknown
    // template variable, no recipients, no channel), so stop here first.
    const notifyProblem = firstNotifyStepProblem(workflowSteps, t);
    if (notifyProblem) {
      toast({
        title: t('workflows.notify.errors.fixSteps', 'Fix the workflow steps'),
        description: notifyProblem,
        variant: 'destructive',
      });
      return;
    }

    // BE #1220: a member-message step that uses a variable the org does not
    // have would be refused at send time for every member, so stop here.
    const variableProblem = await firstMessageStepVariableProblem(workflowSteps, t);
    if (variableProblem) {
      toast({
        title: t('workflows.notify.errors.fixSteps', 'Fix the workflow steps'),
        description: variableProblem,
        variant: 'destructive',
      });
      return;
    }

    console.log('[TCO Template] Starting save, template name:', templateName);
    setSaving(true);
    try {
      const templateData: WorkflowTemplateCreate = {
        template_name: templateName,
        template_type: 'care_coordination',
        description,
        trigger_type: triggerType,
        auto_trigger_enabled: autoTriggerEnabled,
        trigger_sdoh_domains: selectedSdohDomains,
        trigger_min_risk_score: minRiskScore,
        default_channel: defaultChannel,
        max_outreach_attempts: maxOutreachAttempts,
        response_timeout_hours: responseTimeoutHours,
        use_ml_timing: useMlTiming,
        auto_create_referral: autoCreateReferral,
        referral_urgency_default: referralUrgencyDefault,
        referral_service_types: selectedServiceTypes,
        max_providers_to_match: maxProvidersToMatch,
        prefer_network_providers: preferNetworkProviders,
        cbo_only_for_sdoh: cboOnlyForSdoh,
        is_active: isActive,
        target_entity_type: targetEntityType,
        trigger_config: {
          provider_matching_mode: providerMatchingMode,
          max_distance_miles: maxDistanceMiles,
          require_acceptance: requireAcceptance,
          acceptance_timeout_hours: acceptanceTimeoutHours,
          enable_pricing_agent: enablePricingAgent,
          pricing_strategy: pricingStrategy,
          max_negotiation_rounds: maxNegotiationRounds,
          auto_billing: autoBilling,
          default_payment_method: defaultPaymentMethod,
        },
        workflow_steps: workflowSteps.length > 0
          ? workflowSteps.map((s) => ({
              name: s.name,
              step_type: s.step_type,
              order: s.order,
              config: s.config || {},
              form_template_id: s.form_template_id || undefined,
              completion_requires_form: s.completion_requires_form || false,
              documentation_required: s.documentation_required || false,
              document_types: s.document_types || undefined,
              incentive_milestone_code: s.incentive_milestone_code || undefined,
              is_critical: s.is_critical || false,
            }))
          : [
              { name: 'Initial Outreach', step_type: 'outreach', order: 1, config: { channel: defaultChannel } },
              { name: 'Wait for Response', step_type: 'wait', order: 2, config: { timeout_hours: responseTimeoutHours } },
              { name: 'Create Referral', step_type: 'create_referral', order: 3, config: { auto_match: providerMatchingMode === 'auto' } },
            ],
      };

      console.log('[TCO Template] Sending API request:', templateData);
      const result = await tcoWorkflowsAPI.createTemplate(templateData);
      console.log('[TCO Template] API response:', result);

      toast({
        title: 'Template Created',
        description: `Workflow template "${templateName}" has been created successfully.`,
      });

      // Use onSuccess callback if provided, otherwise fall back to navigation
      if (onSuccess) {
        onSuccess(result.id);
      } else {
        navigate('/care/workflows');
      }
    } catch (err: any) {
      console.error('[TCO Template] Failed to create workflow template:', err);
      toast({
        title: 'Error',
        description: workflowSaveErrorMessage(err, t, 'Failed to create workflow template'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = (p: StarterTemplate) => {
    setTemplateName(p.name);
    setDescription(p.description);
    setTriggerType(p.triggerType);
    setWorkflowSteps(p.steps.map((s, i) => ({ name: s.name, step_type: s.step_type, order: i + 1, config: s.config })));
    setActiveTab('steps');
    toast({ title: 'Template loaded', description: `Prefilled from “${p.name}”. Review each step and save.` });
  };

  return (
    <div className="container mx-auto py-6 px-4 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <div className="flex-1">
          {!tabbed && (
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Create Workflow Template
          </h1>
          )}
          <p className="text-gray-500 dark:text-gray-400">
            Configure a complete care coordination workflow with provider matching, pricing, and billing
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Template
        </Button>
      </div>

      {/* Prebuilt starter templates */}
      <Card className="mb-6 border-blue-100 bg-blue-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Workflow className="w-4 h-4 text-blue-600" />
            Start from a prebuilt template
          </CardTitle>
          <CardDescription>
            Prefill a connected, agent-driven workflow — then tune the steps. Or build from scratch below.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-3">
          {STARTER_TEMPLATES.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => applyPreset(p)}
              className="rounded-lg border border-gray-200 bg-white p-3 text-left transition hover:border-blue-400 hover:bg-blue-50/60"
            >
              <div className="text-sm font-semibold text-gray-900">{p.name}</div>
              <div className="mt-1 text-xs text-gray-500">{p.description}</div>
              <div className="mt-2 text-[11px] font-medium text-blue-700">{p.steps.length} steps →</div>
            </button>
          ))}
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-5 w-full">
          <TabsTrigger value="basic" className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />
            Basic
          </TabsTrigger>
          <TabsTrigger value="steps" className="flex items-center gap-2">
            <Workflow className="w-4 h-4" />
            Steps
            {workflowSteps.length > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs">{workflowSteps.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="trigger" className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Triggers
          </TabsTrigger>
          <TabsTrigger value="provider" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Providers
          </TabsTrigger>
          <TabsTrigger value="pricing" className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Pricing
          </TabsTrigger>
        </TabsList>

        {/* Basic Information Tab */}
        <TabsContent value="basic" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Name and describe your workflow template</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="templateName">Template Name *</Label>
                <Input
                  id="templateName"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., SDOH Food Insecurity Outreach"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what this workflow does and when it should be triggered..."
                  rows={3}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Active</Label>
                  <p className="text-sm text-gray-500">Enable this template for use</p>
                </div>
                <Switch checked={isActive} onCheckedChange={setIsActive} />
              </div>
              <div className="space-y-2">
                <Label>Target Entity Type</Label>
                <p className="text-sm text-gray-500">Who should this workflow be used for?</p>
                <div className="flex gap-3 mt-1">
                  {(['member', 'contact', 'both'] as const).map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setTargetEntityType(opt)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                        targetEntityType === opt
                          ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                          : 'border-gray-200 dark:border-gray-700 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <Users className="w-4 h-4" />
                      {opt === 'member' ? 'Members Only' : opt === 'contact' ? 'Contacts Only' : 'Members & Contacts'}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Referral Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Referral Settings</CardTitle>
              <CardDescription>Configure automatic referral creation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-Create Service Referral</Label>
                  <p className="text-sm text-gray-500">
                    Automatically create a service referral when workflow identifies need
                  </p>
                </div>
                <Switch checked={autoCreateReferral} onCheckedChange={setAutoCreateReferral} />
              </div>

              {autoCreateReferral && (
                <>
                  <div className="space-y-2">
                    <Label>Default Urgency Level</Label>
                    <div className="grid grid-cols-3 gap-3">
                      {URGENCY_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setReferralUrgencyDefault(option.value)}
                          className={`p-3 rounded-lg border text-left transition-colors ${
                            referralUrgencyDefault === option.value
                              ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                              : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                          }`}
                        >
                          <span className="font-medium">{option.label}</span>
                          <p className="text-xs text-gray-500 mt-1">{option.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Service Types for Referral</Label>
                    <p className="text-sm text-gray-500 mb-2">
                      Select which service types this workflow can create referrals for
                    </p>
                    {loadingServiceTypes ? (
                      <div className="flex items-center gap-2 p-3 border rounded-md">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm text-gray-500">Loading service types...</span>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 border rounded-md">
                        {serviceTypes.slice(0, 20).map((st) => (
                          <Badge
                            key={st.code}
                            variant={selectedServiceTypes.includes(st.code) ? 'default' : 'outline'}
                            className="cursor-pointer"
                            onClick={() => toggleServiceType(st.code)}
                          >
                            {st.name}
                          </Badge>
                        ))}
                        {serviceTypes.length > 20 && (
                          <span className="text-xs text-gray-500 self-center">
                            +{serviceTypes.length - 20} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Workflow Steps Tab */}
        <TabsContent value="steps" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Workflow Steps</CardTitle>
                  <CardDescription>
                    Define each step in the workflow. Add forms, outreach, assessments, decisions, and more.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {/* View mode toggle */}
                  <div className="flex border rounded-md overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setStepsViewMode('list')}
                      className={`px-3 py-1.5 text-xs font-medium ${stepsViewMode === 'list' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      List
                    </button>
                    <button
                      type="button"
                      onClick={() => setStepsViewMode('flow')}
                      className={`px-3 py-1.5 text-xs font-medium ${stepsViewMode === 'flow' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      Flow
                    </button>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      setStepsViewMode('list');
                      setAddingStep(true);
                      setEditingStepIndex(null);
                      setNewStepType('');
                      setNewStepName('');
                      setNewStepConfig({});
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Step
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Flow View */}
              {stepsViewMode === 'flow' && workflowSteps.length > 0 && (
                <WorkflowFlowView
                  steps={workflowSteps}
                  onEditStep={(idx) => { setStepsViewMode('list'); startEditStep(idx); }}
                  onRemoveStep={removeStep}
                  onAddAfter={(idx) => {
                    setStepsViewMode('list');
                    setAddingStep(true);
                    setEditingStepIndex(null);
                    setNewStepType('');
                    setNewStepName('');
                    setNewStepConfig({});
                  }}
                />
              )}

              {/* List View */}
              {stepsViewMode === 'list' && workflowSteps.length === 0 && !addingStep && (
                <div className="text-center py-12 text-gray-500">
                  <Workflow className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No steps configured</p>
                  <p className="text-sm mt-1">Click "Add Step" to build your workflow with outreach, forms, assessments, and more.</p>
                </div>
              )}

              {/* Existing steps (list view only) */}
              {stepsViewMode === 'list' && workflowSteps.map((step, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 p-4 border rounded-lg ${
                    editingStepIndex === index ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex flex-col items-center gap-1 pt-1">
                    <button
                      type="button"
                      onClick={() => moveStep(index, 'up')}
                      className="p-0.5 text-gray-400 hover:text-gray-600"
                      disabled={index === 0}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <span className="text-xs font-bold text-gray-500 w-6 text-center">{step.order}</span>
                    <button
                      type="button"
                      onClick={() => moveStep(index, 'down')}
                      className="p-0.5 text-gray-400 hover:text-gray-600"
                      disabled={index === workflowSteps.length - 1}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>

                  {editingStepIndex === index ? (
                    <div className="flex-1 space-y-3">
                      <Input
                        value={newStepName}
                        onChange={(e) => setNewStepName(e.target.value)}
                        placeholder="Step name"
                        className="font-medium"
                      />
                      <StepTypePicker
                        selectedType={newStepType}
                        onSelect={(type, _option) => setNewStepType(type)}
                        variant="dropdown"
                      />
                      {newStepType && (
                        <div className="border rounded-lg p-3 bg-white">
                          <StepConfigurationPanel
                            stepTypeValue={newStepType}
                            baseType={newStepType.split('_')[0] as any}
                            config={newStepConfig}
                            onChange={setNewStepConfig}
                            memberBound={targetEntityType !== 'contact'}
                          />
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => updateStep(index)} disabled={notifyStepIssues(newStepType, newStepConfig).length > 0}>Save</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingStepIndex(null)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{step.name}</span>
                        <Badge variant="outline" className="text-xs">{step.step_type.replace(/_/g, ' ')}</Badge>
                        {step.form_template_id && (
                          <Badge variant="secondary" className="text-xs">Form attached</Badge>
                        )}
                        {step.is_critical && (
                          <Badge variant="destructive" className="text-xs">Critical</Badge>
                        )}
                      </div>
                      {step.config?.message_body && (
                        <p className="text-xs text-gray-500 mt-1 truncate">{step.config.message_body}</p>
                      )}
                    </div>
                  )}

                  {editingStepIndex !== index && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => startEditStep(index)}>Edit</Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removeStep(index)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}

              {/* Add new step form (list view) */}
              {stepsViewMode === 'list' && addingStep && (
                <div className="p-4 border-2 border-dashed border-blue-300 rounded-lg bg-blue-50/50 space-y-3">
                  <div className="text-sm font-medium text-blue-700">New Step #{workflowSteps.length + 1}</div>
                  <Input
                    value={newStepName}
                    onChange={(e) => setNewStepName(e.target.value)}
                    placeholder="Step name (e.g., Send Consent Form, SDOH Assessment, Initial Outreach)"
                    className="font-medium"
                  />
                  <StepTypePicker
                    selectedType={newStepType}
                    onSelect={(type, _option) => setNewStepType(type)}
                    variant="dropdown"
                  />
                  {newStepType && (
                    <div className="border rounded-lg p-3 bg-white">
                      <StepConfigurationPanel
                        stepTypeValue={newStepType}
                        baseType={newStepType.split('_')[0] as any}
                        config={newStepConfig}
                        onChange={setNewStepConfig}
                        memberBound={targetEntityType !== 'contact'}
                      />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" onClick={addStep} disabled={!newStepType || !newStepName.trim() || notifyStepIssues(newStepType, newStepConfig).length > 0}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Step
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setAddingStep(false)}>Cancel</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trigger Configuration Tab */}
        <TabsContent value="trigger" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Trigger Configuration</CardTitle>
              <CardDescription>Define when this workflow should be triggered</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Trigger Type</Label>
                <div className="grid grid-cols-2 gap-3">
                  {TRIGGER_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setTriggerType(type.value)}
                      className={`p-4 rounded-lg border text-left transition-colors ${
                        triggerType === type.value
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={triggerType === type.value ? 'text-indigo-600' : 'text-gray-400'}>
                          {type.icon}
                        </span>
                        <span className="font-medium">{type.label}</span>
                      </div>
                      <p className="text-sm text-gray-500">{type.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-Trigger</Label>
                  <p className="text-sm text-gray-500">Automatically start workflow when conditions are met</p>
                </div>
                <Switch checked={autoTriggerEnabled} onCheckedChange={setAutoTriggerEnabled} />
              </div>

              {triggerType === 'clinical_signal_detected' && (
                <div className="space-y-3 rounded-lg border border-rose-200 bg-rose-50/50 p-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-rose-900">
                    <HeartPulse className="h-4 w-4" />
                    Clinical signal initiators
                  </div>
                  <p className="text-sm text-gray-600">
                    This workflow opens automatically when any of these health signals are detected for
                    a member. The clinical→social loop then runs its connected steps (clinician gate →
                    social referral(s) → multi-channel member outreach → PCP loop-back).
                  </p>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>• <strong>Approved AI health insight</strong> — a clinician approves an insight in Health Connect</li>
                    <li>• <strong>EHR / claims diagnosis</strong> — ICD-10 codes that map to a social need</li>
                    <li>• <strong>Observation threshold</strong> — vitals/labs crossing a configured threshold</li>
                  </ul>
                  <p className="text-xs text-gray-500">
                    Add a <strong>Clinician Approval Gate</strong> step (Clinical → Social) so referrals and
                    outreach only proceed after human review.
                  </p>
                </div>
              )}

              {triggerType === 'sdoh_threshold' && (
                <>
                  <div className="space-y-2">
                    <Label>SDOH Domains to Monitor</Label>
                    <p className="text-sm text-gray-500 mb-2">
                      Select which SDOH domains should trigger this workflow
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {SDOH_DOMAINS.map((domain) => (
                        <Badge
                          key={domain.value}
                          variant={selectedSdohDomains.includes(domain.value) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => toggleSdohDomain(domain.value)}
                        >
                          {domain.label}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="minRiskScore">Minimum Risk Score</Label>
                    <p className="text-sm text-gray-500">
                      Trigger when SDOH risk score is at or above this threshold (0-100)
                    </p>
                    <Input
                      id="minRiskScore"
                      type="number"
                      min={0}
                      max={100}
                      value={minRiskScore}
                      onChange={(e) => setMinRiskScore(parseInt(e.target.value) || 0)}
                      className="w-32"
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Provider Matching Tab */}
        <TabsContent value="provider" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Provider Matching Mode</CardTitle>
              <CardDescription>Configure how providers are matched to referrals</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {PROVIDER_MATCHING_MODES.map((mode) => (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setProviderMatchingMode(mode.value)}
                    className={`p-4 rounded-lg border text-left transition-colors ${
                      providerMatchingMode === mode.value
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                        : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={providerMatchingMode === mode.value ? 'text-indigo-600' : 'text-gray-400'}>
                        {mode.icon}
                      </span>
                      <span className="font-medium">{mode.label}</span>
                    </div>
                    <p className="text-sm text-gray-500">{mode.description}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Matching Parameters</CardTitle>
              <CardDescription>Fine-tune the provider matching algorithm</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="maxProviders">Max Providers to Match</Label>
                  <Input
                    id="maxProviders"
                    type="number"
                    min={1}
                    max={20}
                    value={maxProvidersToMatch}
                    onChange={(e) => setMaxProvidersToMatch(parseInt(e.target.value) || 5)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="maxDistance">Max Distance (miles)</Label>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <Input
                      id="maxDistance"
                      type="number"
                      min={1}
                      max={100}
                      value={maxDistanceMiles}
                      onChange={(e) => setMaxDistanceMiles(parseInt(e.target.value) || 25)}
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Prefer Network Providers</Label>
                  <p className="text-sm text-gray-500">
                    Prioritize contracted network providers in matching
                  </p>
                </div>
                <Switch checked={preferNetworkProviders} onCheckedChange={setPreferNetworkProviders} />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>CBO Only for SDOH Services</Label>
                  <p className="text-sm text-gray-500">
                    Route SDOH-related services only to CBOs (not healthcare providers)
                  </p>
                </div>
                <Switch checked={cboOnlyForSdoh} onCheckedChange={setCboOnlyForSdoh} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Provider Acceptance</CardTitle>
              <CardDescription>Configure provider acceptance workflow</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Require Provider Acceptance</Label>
                  <p className="text-sm text-gray-500">
                    Provider must accept referral before service begins
                  </p>
                </div>
                <Switch checked={requireAcceptance} onCheckedChange={setRequireAcceptance} />
              </div>

              {requireAcceptance && (
                <div className="space-y-2">
                  <Label htmlFor="acceptanceTimeout">Acceptance Timeout (hours)</Label>
                  <p className="text-sm text-gray-500">
                    Time provider has to accept before escalation
                  </p>
                  <Input
                    id="acceptanceTimeout"
                    type="number"
                    min={1}
                    max={168}
                    value={acceptanceTimeoutHours}
                    onChange={(e) => setAcceptanceTimeoutHours(parseInt(e.target.value) || 48)}
                    className="w-32"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pricing & Billing Tab */}
        <TabsContent value="pricing" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>AI Pricing Agent</CardTitle>
              <CardDescription>Configure automated pricing negotiation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Enable Pricing Agent</Label>
                  <p className="text-sm text-gray-500">
                    Use AI to negotiate pricing with providers automatically
                  </p>
                </div>
                <Switch checked={enablePricingAgent} onCheckedChange={setEnablePricingAgent} />
              </div>

              {enablePricingAgent && (
                <>
                  <div className="space-y-2">
                    <Label>Negotiation Strategy</Label>
                    <Select value={pricingStrategy} onValueChange={setPricingStrategy}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aggressive">Aggressive - Prioritize lowest cost</SelectItem>
                        <SelectItem value="balanced">Balanced - Balance cost and speed</SelectItem>
                        <SelectItem value="accommodating">Accommodating - Prioritize acceptance rate</SelectItem>
                        <SelectItem value="urgent">Urgent - Fast acceptance, flexible pricing</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="maxRounds">Max Negotiation Rounds</Label>
                    <Input
                      id="maxRounds"
                      type="number"
                      min={1}
                      max={10}
                      value={maxNegotiationRounds}
                      onChange={(e) => setMaxNegotiationRounds(parseInt(e.target.value) || 5)}
                      className="w-32"
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Billing Configuration</CardTitle>
              <CardDescription>Configure automatic billing and payment processing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-Generate Billing</Label>
                  <p className="text-sm text-gray-500">
                    Automatically create billing records when service is completed
                  </p>
                </div>
                <Switch checked={autoBilling} onCheckedChange={setAutoBilling} />
              </div>

              {autoBilling && (
                <div className="space-y-2">
                  <Label>Default Payment Method</Label>
                  <Select value={defaultPaymentMethod} onValueChange={setDefaultPaymentMethod}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="invoice">Invoice Billing</SelectItem>
                      <SelectItem value="ach">ACH Payment</SelectItem>
                      <SelectItem value="prepaid_card">Prepaid Card</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
